import { eventBus } from '@/utils/eventBus';
import { logger } from '@/utils/logger';

// 允许存储任意 notification 结构
export type Notification = any;

export type ChatNotificationItem = {
  isRead: boolean;
  timestamp: string;
  uid: string;
  content: any;
};

type NotificationListener = (notifications: ChatNotificationItem[]) => void;

class NotificationManager {
  // chatId -> listeners
  private listeners: Map<string, Set<NotificationListener>> = new Map();
  // chatId -> chatNotificationItems
  private chatNotificationItems: Map<string, ChatNotificationItem[]> = new Map();
  // chatId -> has new notifications
  private hasNewNotificationItems: Map<string, boolean> = new Map();

  constructor() {
    this.initEventListeners();
  }

  private initEventListeners() {
    eventBus.on('chat:newNotification', (params: { chatId: string, content: any, isRead: boolean, timestamp: string, uid: string }) => {
      const { chatId, content, isRead, timestamp, uid } = params;

      if (!chatId) {
        logger.warn('NotificationManager: chatId is required for newNotification');
        return;
      }
      const chatNotificationItem: ChatNotificationItem = {
        isRead: isRead,
        timestamp: timestamp,
        uid: uid,
        content: content,
      };
      this.addNotification(chatId, chatNotificationItem);
    });
  }

  public addNotification(chatId: string, chatNotificationItem: ChatNotificationItem) {
    const list = this.chatNotificationItems.get(chatId) || [];
    list.unshift(chatNotificationItem);
    this.chatNotificationItems.set(chatId, list);
    this.hasNewNotificationItems.set(chatId, true);
    this.notifyListeners(chatId);
  }

  private notifyListeners(chatId: string) {
    const listeners = this.listeners.get(chatId);
    if (!listeners) return;
    const notifications = this.chatNotificationItems.get(chatId) || [];
    listeners.forEach(listener => {
      try {
        listener([...notifications]);
      } catch (error) {
        logger.error('Error in notification listener:', error);
      }
    });
  }

  // 订阅指定 chatId 的通知更新
  subscribe(chatId: string, listener: NotificationListener): () => void {
    if (!this.listeners.has(chatId)) {
      this.listeners.set(chatId, new Set());
    }
    this.listeners.get(chatId)!.add(listener);
    // 立即通知当前状态
    listener([...(this.chatNotificationItems.get(chatId) || [])]);
    // 返回取消订阅函数
    return () => {
      this.listeners.get(chatId)?.delete(listener);
    };
  }

  // 获取指定 chatId 的所有通知
  getNotifications(chatId: string): ChatNotificationItem[] {
    return [...(this.chatNotificationItems.get(chatId) || [])];
  }

  // 获取指定 chatId 的分页通知
  getNotificationsPaged(chatId: string, offset: number, limit: number): ChatNotificationItem[] {
    const all = this.chatNotificationItems.get(chatId) || [];
    return all.slice(offset, offset + limit);
  }

  // 检查指定 chatId 是否有新通知
  hasNew(chatId: string): boolean {
    return !!this.hasNewNotificationItems.get(chatId);
  }

  // 标记指定 chatId 的通知为已读
  markAsRead(chatId: string): void {
    this.hasNewNotificationItems.set(chatId, false);
  }

  // 清空指定 chatId 的所有通知
  clear(chatId: string): void {
    this.chatNotificationItems.set(chatId, []);
    this.hasNewNotificationItems.set(chatId, false);
    this.notifyListeners(chatId);
  }

  // 移除指定 chatId 的特定通知
  removeNotification(chatId: string, uid: string): void {
    const list = this.chatNotificationItems.get(chatId) || [];
    this.chatNotificationItems.set(chatId, list.filter((n: ChatNotificationItem) => n.uid !== uid));
    this.notifyListeners(chatId);
  }
}

export const notificationManager = new NotificationManager(); 